import { useState, useEffect } from 'react'
import * as yup from 'yup'
import {
  validateFunction,
  findError,
  deleteError,
} from '@/utils/form-errors/validate-form'
import { ErrorWrapper } from '@/components/forms/validation-errors/styled'
import storage from '@/utils/storage'
import { useNavigate } from 'react-router-dom'
import {
  Stepper,
  Pagination,
  Title,
  Divider,
  Block,
  Item,
  Button,
} from '../styled'
import { StepThreeParams } from './types'
import ClipLoader from 'react-spinners/ClipLoader'
import InputComponent from '../input'
import { TitleTooltip } from '../tooltip-content'
import { axios } from '@/lib/axios'
import { useAppSelector } from '@/app/hooks'

const ThirdStep = ({
  prevState,
  previousState,
  setThirdStepState,
}: StepThreeParams) => {
  const navigate = useNavigate()
  const [state, setState] = useState({})
  const [isSubmit, setIsSubmit] = useState(false)
  const [error, setError] = useState(false)
  const [requirements, setRequirements] = useState(previousState?.useCase || '')
  const [validationErrors, setValidationErrors] = useState<
    yup.ValidationError[]
  >([])
  const baseUrl = process.env.API_BASE_URL
  const supportEmail = process.env.SUPPORT_EMAIL
  const userMail = useAppSelector((state) => state.authUser.userMail)
  const token = storage.getToken()

  const validateSchema = {
    requirements: yup
      .string()
      .required('Requirements are required.')
      .min(140, "Requirements can't be shorter than 140 characters"),
  }

  type stateType = {
    requirements: string
  }

  const internalValidateFunction = async (
    value: stateType | stateType[keyof stateType],
    lookup?: keyof stateType,
  ): Promise<boolean> => {
    const newErrors = await validateFunction<stateType>(
      value,
      validateSchema,
      lookup,
    )
    setValidationErrors(newErrors)
    return newErrors.length === 0
  }

  const internalDeleteError = (identifier: string) => {
    deleteError(identifier, validationErrors, setValidationErrors)
  }

  const internalFindError = (identifier: string) => {
    const error = findError(identifier, validationErrors)
    return !!error && <ErrorWrapper>{error.message}</ErrorWrapper>
  }

  const thirdStepValues = {
    useCase: requirements,
  }

  useEffect(() => {
    setState(
      Object.assign(state, prevState, thirdStepValues, {
        typeOfData: 'Dataset',
        active: true,
      }),
    )
    if (setThirdStepState) {
      setThirdStepState(thirdStepValues)
    }
  }, [prevState, thirdStepValues])

  const onClick = async () => {
    const isValid = await internalValidateFunction({
      requirements,
    })
    if (!isValid) {
      return
    }

    setIsSubmit(true)
    axios
      .post(`${baseUrl}/postings`, state)
      .then(function (response: any) {
        setIsSubmit(false)
        setError(false)
        sendPostCreationEmailToKamba(prevState.title)
        navigate('/posting')
      })
      .catch(function (error: any) {
        setIsSubmit(false)
        setError(true)
      })
  }

  const sendPostCreationEmailToKamba = (postTitle: string) => {
    const res = axios
      .post(`${baseUrl}/send-email-no-template`, {
        from: supportEmail,
        to: supportEmail,
        subject: 'New Posting Created',
        content: `The posting ${postTitle} has been created by the user: ${userMail}`,
      })
      .then(function (response: any) {
        return response
      })
      .catch(function (error: any) {
        console.log(error)
      })
    return res
  }

  return (
    <Stepper>
      <Block>
        <Pagination>Step 3/3</Pagination>
        <Title>Requirements</Title>
        <Divider />
        <Item
          onFocus={() => {
            internalDeleteError('requirements')
          }}
          onBlur={(e: any) =>
            internalValidateFunction(requirements, 'requirements')
          }
        >
          <InputComponent
            title="Your requirements in detail"
            minCharacters={140}
            isTextArea
            value={requirements}
            getValue={(e: any) => setRequirements(e.target.value)}
          >
            <TitleTooltip />
          </InputComponent>
          {internalFindError('requirements')}
        </Item>
      </Block>
      <Button onClick={onClick}>
        {!isSubmit ? (
          `Post your Request`
        ) : (
          <ClipLoader color="#ffffff" size={15} />
        )}
      </Button>
      {validationErrors.length > 0 && (
        <ErrorWrapper>You must resolve the errors to continue</ErrorWrapper>
      )}
      {error && <ErrorWrapper>An error has occurred</ErrorWrapper>}
    </Stepper>
  )
}

export default ThirdStep
